import { notifications } from '@mantine/notifications';
import { IconMoodPuzzled } from '@tabler/icons-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FirewallSecurityGroupResponse } from '../../@types/firewall-security-group';
import { Pagination } from '../../@types/pagination';
import { Endpoints } from '../../apis';
import { isNetworkError } from '../../libs/connectionNotifier';

export function useSecurityGroupsInfiniteQuery(pageSize = 20, enabled = true) {
    const { t } = useTranslation('notifications');
    const hasShownError = useRef(false);

    const query = useInfiniteQuery({
        queryKey: ['api.v1.firewalls.security-groups.infinite', pageSize],
        queryFn: ({ pageParam }): Promise<Pagination<FirewallSecurityGroupResponse>> =>
            Endpoints.v1.Firewalls.SecurityGroups.listSecurityGroups({
                page: pageParam,
                limit: pageSize,
            }),
        initialPageParam: 1,
        getNextPageParam: (last) => (last.page < last.total_pages ? last.page + 1 : undefined),
        enabled,
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    });

    const { error } = query;

    useEffect(() => {
        if (error && !isNetworkError(error) && !hasShownError.current) {
            hasShownError.current = true;
            notifications.show({
                title: t('query.failed'),
                message: t('query.securityGroupsFailedMessage'),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });
        }

        if (!error) {
            hasShownError.current = false;
        }
    }, [error, t]);

    return query;
}
